import { takeLatest, take, call, put, delay, fork, spawn, join, cancel, select } from 'redux-saga/effects';
import {
  LOAD_ORDER_DETAILS, LEAVE_ORDER_DETAILS, OpenQueryGridName,
  ClosedQueryGridName, ORDER_CHASE, PACKAGE_CHASE, PACKAGE_CANCEL, LOAD_PACKAGE_REPORTS,
  ORDER_DETAILS_ADD_SELECTED_PACKAGES_TO_ORDER, ORDER_CANCEL,
  ORDER_DETAILS_SUBMIT_RAISE_QUERY
} from './constants';
import {
  loadOrder, orderLoaded, orderLoadingError,
  setAlertDatasource,
  chaseOrderSuccess, chaseOrderFail,
  loadPackageReportsSuccess, loadPackageReportsError,
  chasePackageSuccess, chasePackageFail, chaseOrderHideLabel, chasePackageHideLabel,
  addSelectedPackagesToOrderSuccess, cancelPackageSuccess, cancelPackageFail,
  submitQuerySucceed, submitQueryFail, cancelOrderSuccess, cancelOrderFail
} from './actions';
import { showAppError } from 'containers/App/actions';
import config from 'utils/config';
import request from 'utils/request';
//import { replyToQueryRequestSaga } from '../Queries/sagas';
import { uploadDeleteRequestWatcherSaga } from '../../components/UploadFile/sagas';
import { setQueryDataSource } from '../Queries/actions';
import { uploadInit } from '../../components/UploadFile/actions';
import { OrderDetailsUploadChannel } from './constants';
import { isSameOrderId } from './reducer';
import { selectRequestedOrderId } from './selectors';

//
// API call to load order data 
//
export function* loadOrderDetailsRequest(action) {
  const requestURL = `${config.webApiUrl}/order/${action.orderId}`;
  const alertRequestURL = `${config.webApiUrlAlerts}/Alerts/getAlertsForOrder?orderId=${action.orderId}`;
  try {
    // Call our request helper (see 'utils/request')
    const order = yield call(request.getAuth, requestURL);
    yield put(orderLoaded(order));
    yield put(setQueryDataSource(OpenQueryGridName, { queries: order.queries }));
    yield put(setQueryDataSource(ClosedQueryGridName, { queries: order.closedQueries }));
    yield put(uploadInit(order.attachments, OrderDetailsUploadChannel));
    const alertsString = yield call(request.getAuth, alertRequestURL);

    //// console.log(alertsString);
    // create dictionary from order's packages
    var packageDict = {};
    order.packages.forEach(function (element) {
      packageDict[element.id] = true;
    });

    // push only those alerts which are not selected yet
    var filteredAlerts = [];
    var allerts = JSON.parse(alertsString);
    allerts.forEach(function (element) {
      if (packageDict[element.id] !== true) {
        filteredAlerts.push(element);
      }
    });
    yield put(setAlertDatasource(filteredAlerts));
  }
  catch (err) {
    // console.log('error');
    // console.log(err);
    yield put(orderLoadingError(err));
    yield put(showAppError(err));
  }
}

// Only the latest load runs, and leaving the page stops it, so a late load doesn't update the page's data
// (reused by Add More Products) or show its failure on another page.
export function* loadOrderSaga() {
  let load;
  while (true) {
    const action = yield take([LOAD_ORDER_DETAILS, LEAVE_ORDER_DETAILS]);
    if (load) {
      yield cancel(load);
    }
    load = action.type === LOAD_ORDER_DETAILS ? yield fork(loadOrderDetailsRequest, action) : undefined;
  }
}
//
// Makes a server request for work on the order on screen and reports its failure. The request runs outside
// the order scope (see orderScopeSaga): if the user opens another order before it completes, the caller is
// cancelled and its page updates are dropped, but a failure is still shown. Throws the request's error;
// callers pass what they catch to showErrorUnlessShown, so an error of their own is shown too, but only once.
//
const shownErrors = new WeakSet();

export function* orderRequest(fn, ...args) {
  const outcome = yield join(yield spawn(requestReportingFailure, fn, args));
  if (outcome.failed) {
    shownErrors.add(outcome.err);
    throw outcome.err;
  }
  return outcome.result;
}

function* requestReportingFailure(fn, args) {
  try {
    return { result: yield call(fn, ...args) };
  }
  catch (err) {
    // A request can reject without a reason (utils/request does when the token refresh fails)
    const reported = err instanceof Object ? err : new Error('Request failed');
    yield put(showAppError(reported));
    return { failed: true, err: reported };
  }
}

export function* showErrorUnlessShown(err) {
  if (!(err instanceof Object && shownErrors.has(err))) {
    yield put(showAppError(err));
  }
}

//
// API call to get report files
//
export function* loadReportsRequest(action) {
  const requestURL = `${config.webApiUrl}/order/getReports/${action.orderId}/${action.packageId}`;
  try {
    // Call our request helper (see 'utils/request')
    const reports = yield call(orderRequest, request.getAuth, requestURL);
    yield put(loadPackageReportsSuccess(reports));
  }
  catch (err) {
    yield put(loadPackageReportsError(err));
    yield call(showErrorUnlessShown, err);
  }
}

export function* loadReportsSaga() {
  yield takeLatest(LOAD_PACKAGE_REPORTS, loadReportsRequest);
}

//
// Chase order saga. Orders can also be chased from the Orders and Home lists, so this runs outside the order
// scope and only refreshes the queries grid when the chased order is the one on screen.
//
export function* chaseOrderSaga() {
  yield takeLatest(ORDER_CHASE, chaseOrderRequest);
}

export function* chaseOrderRequest(action) {
  const requestURL = `${config.webApiUrl}/order/chase/`;
  const queryUrl = `${config.webApiUrl}/query/getOpenQueriesForOrder?orderId=${action.info.orderId}`;
  const query = {
    orderId: action.info.orderId,
    dueDate: action.info.dueDate
  };
  try {

    const result = yield call(orderRequest, request.postAuth, requestURL, query);
    yield put(chaseOrderSuccess(query.orderId));
    if (isSameOrderId(yield select(selectRequestedOrderId()), query.orderId)) {
      var queries = yield call(orderRequest, request.getAuth, queryUrl);
      if (isSameOrderId(yield select(selectRequestedOrderId()), query.orderId)) {
        yield put(setQueryDataSource(OpenQueryGridName, { queries: queries }));
      }
    }
    yield delay(1000);
    yield put(chaseOrderHideLabel(query.orderId));
    chasePackageHideLabel
  }
  catch (err) {
    console.log(err);
    yield put(chaseOrderFail(query.orderId));
    yield call(showErrorUnlessShown, err);
  }
}

//
// Cancel order saga
//
export function* cancelOrderSaga() {
  yield takeLatest(ORDER_CANCEL, cancelOrderRequest);
}

export function* cancelOrderRequest(action) {
  const requestURL = `${config.webApiUrl}/order/cancel`;
  const queryUrl = `${config.webApiUrl}/query/getOpenQueriesForOrder?orderId=${action.item.orderId}`;
  try {

    const result = yield call(orderRequest, request.postAuth, requestURL, action.item);
    var queries = yield call(orderRequest, request.getAuth, queryUrl);
    yield put(cancelOrderSuccess(action.item.orderId));
    yield put(setQueryDataSource(OpenQueryGridName, { queries: queries }));
  }
  catch (err) {
    yield put(cancelOrderFail(action.item.orderId));
    yield call(showErrorUnlessShown, err);
  }
}

//
// Chase package saga
//
export function* chasePackageSaga() {
  yield takeLatest(PACKAGE_CHASE, chasePackageRequest);
}

export function* chasePackageRequest(action) {
  const requestURL = `${config.webApiUrl}/package/chase/`;
  const queryUrl = `${config.webApiUrl}/query/getOpenQueriesForOrder?orderId=${action.item.orderId}`;
  try {

    const result = yield call(orderRequest, request.postAuth, requestURL, action.item);
    var queries = yield call(orderRequest, request.getAuth, queryUrl);
    yield put(chasePackageSuccess(action.item.packageId));
    yield put(setQueryDataSource(OpenQueryGridName, { queries: queries }));
    yield delay(1000);
    yield put(chasePackageHideLabel(action.item.packageId));
  }
  catch (err) {
    // console.log(err);
    yield put(chasePackageFail(action.item.packageId));
    yield call(showErrorUnlessShown, err);
  }
}


//
// Cancel package saga
//
export function* cancelPackageSaga() {
  yield takeLatest(PACKAGE_CANCEL, cancelPackageRequest);
}

export function* cancelPackageRequest(action) {
  const requestURL = `${config.webApiUrl}/package/cancel`;
  const queryUrl = `${config.webApiUrl}/query/getOpenQueriesForOrder?orderId=${action.item.orderId}`;
  try {

    const result = yield call(orderRequest, request.postAuth, requestURL, action.item);
    var queries = yield call(orderRequest, request.getAuth, queryUrl);
    yield put(cancelPackageSuccess(action.item.packageId));
    yield put(setQueryDataSource(OpenQueryGridName, { queries: queries }));
    //yield delay(1000);
    ////yield put(chasePackageHideLabel(action.item.packageId));
  }
  catch (err) {
    yield put(cancelPackageFail(action.item.packageId));
    yield call(showErrorUnlessShown, err);
  }
}

//
// Adds selected packages(alerts) to order
//
export function* addSelectedPackagesRequest(action) {
  const requestURL = `${config.webApiUrl}/order/addPackages`;
  try {
    // Call our request helper (see 'utils/request')
    const result = yield call(orderRequest, request.postAuth, requestURL, action.payload);
    yield put(addSelectedPackagesToOrderSuccess(action.payload, result.extraAmount));
    yield put(loadOrder(action.payload.orderId));
    // todo:  
    //window.location.href=`/order/${action.payload.orderId}`
  }
  catch (err) {
    yield call(showErrorUnlessShown, err);
  }
}

export function* addSelectedPackagesSaga() {
  yield takeLatest(ORDER_DETAILS_ADD_SELECTED_PACKAGES_TO_ORDER, addSelectedPackagesRequest);
}


//
// Creates new query which user creates using 'Raise Query' button
//
export function* createQueryRequest(action) {
  const requestURL = `${config.webApiUrl}/query/addQuery`;
  try {
    // Call our request helper (see 'utils/request')
    const result = yield call(orderRequest, request.postAuth, requestURL, action.queryDto);
    yield put(submitQuerySucceed(action.gridName, result));
  }
  catch (err) {
    yield put(submitQueryFail(action.gridName, action.queryDto));
    yield call(showErrorUnlessShown, err);
  }
}

export function* createQuerySaga() {
  yield takeLatest(ORDER_DETAILS_SUBMIT_RAISE_QUERY, createQueryRequest);
}

//
// Watchers for work done on the order on screen. Their in-flight requests belong to that order, so they
// run in one scope that orderScopeSaga cancels when a different order is requested (the page is reused
// for /order/A -> /order/B, e.g. from a notification) or the page is left. A cancelled task's server request
// still completes and a failure is still shown (see orderRequest); only its follow-up (refreshing the page,
// opening the reports dialog, ...) is dropped, so A's results never land on B's page.
//
function* orderScopedSagas() {
  yield fork(uploadDeleteRequestWatcherSaga, orderRequest);
  yield fork(chasePackageSaga);
  yield fork(cancelPackageSaga);
  yield fork(loadReportsSaga);
  yield fork(addSelectedPackagesSaga);
  yield fork(createQuerySaga);
  yield fork(cancelOrderSaga);
}

export function* orderScopeSaga() {
  let scope = yield fork(orderScopedSagas);
  let scopeOrderId = yield select(selectRequestedOrderId());
  while (true) {
    const action = yield take([LOAD_ORDER_DETAILS, LEAVE_ORDER_DETAILS]);
    // Reloading the same order (e.g. after adding products) keeps its work running; leaving the page doesn't.
    if (action.type === LEAVE_ORDER_DETAILS || !isSameOrderId(scopeOrderId, action.orderId)) {
      yield cancel(scope);
      scope = yield fork(orderScopedSagas);
      scopeOrderId = action.orderId;
    }
  }
}

// Bootstrap sagas
export default function* rootSaga(){
  yield fork(loadOrderSaga);
  yield fork(chaseOrderSaga);
  //yield fork(replyToQueryRequestSaga);
  //yield fork(uploadRequestWatcherSaga);
  yield fork(orderScopeSaga);
}
